import { useCallback, useMemo, useEffect, useState } from "react";
import {
  fetchShopsBySuppliers as apiFetchShopsBySuppliers,
  updateShopStatuses as apiUpdateShopStatuses,
} from "../services/shopsBySuppliersService";

// Хук керує логікою та станом для налаштувань "магазин-постачальник"
const useShopBySuppliers = () => {
  const [shops, setShops] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [shopSuppliersMap, setShopSuppliersMap] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  // Мемоізуємо перетворення списку магазинів у формат для Dropdown
  const shopOptions = useMemo(
    () =>
      shops.map(({ shops_id, shops_name }) => ({
        value: shops_id,
        label: shops_name,
      })),
    [shops]
  );

  // Мемоізуємо перетворення списку постачальників у формат для Dropdown
  const supplierOptions = useMemo(
    () =>
      suppliers.map(({ suppliers_id, suppliers_name }) => ({
        value: suppliers_id,
        label: suppliers_name,
      })),
    [suppliers]
  );

  // Завантажує та трансформує дані магазин-постачальник з сервера
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await apiFetchShopsBySuppliers();

      // Використовуємо Map для ефективного отримання унікальних магазинів та постачальників
      const shopsMap = new Map();
      const suppliersMap = new Map();
      data.forEach(({ shops_id, shops_name, suppliers_id, suppliers_name }) => {
        shopsMap.set(shops_id, { shops_id, shops_name });
        suppliersMap.set(suppliers_id, { suppliers_id, suppliers_name });
      });

      // Сортуємо та зберігаємо унікальні списки
      setShops(
        Array.from(shopsMap.values()).sort((a, b) =>
          a.shops_name.localeCompare(b.shops_name)
        )
      );
      setSuppliers(
        Array.from(suppliersMap.values()).sort((a, b) =>
          a.suppliers_name.localeCompare(b.suppliers_name)
        )
      );

      // Будуємо об'єкт-мапу для швидкого доступу до постачальників кожного магазину
      const mapping = {};
      data.forEach(({ shops_id, suppliers_id, status }) => {
        if (status) {
          if (!mapping[shops_id]) mapping[shops_id] = [];
          mapping[shops_id].push(suppliers_id);
        }
      });
      setShopSuppliersMap(mapping);
    } catch (err) {
      console.error("Помилка отримання даних:", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Ефект для початкового завантаження даних при монтуванні
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Надсилає зміни на сервер та оновлює дані
  const applyChanges = useCallback(
    async (selectedShop, selectedSupplierIds) => {
      // Готуємо масив оновлень: для кожного постачальника вказуємо його новий статус
      const updates = supplierOptions.map(({ value: suppliers_id }) => ({
        suppliers_id,
        status: selectedSupplierIds.includes(suppliers_id),
      }));
      await apiUpdateShopStatuses(selectedShop, updates);
      await fetchData();
    },
    [supplierOptions, fetchData]
  );

  return {
    shopOptions,
    supplierOptions,
    shopSuppliersMap,
    isLoading,
    applyChanges,
  };
};

export default useShopBySuppliers;
